import { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { FiChevronDown, FiUploadCloud, FiDownload, FiCheck } from 'react-icons/fi';
import { BsFileImage } from 'react-icons/bs';
import SEO from '../components/SEO';
import { imageConverterSchemas } from '../schemas';
import './Workspace.css';

const Workspace = () => {
  const [searchParams] = useSearchParams();
  const fromParam = searchParams.get('from');
  const toParam = searchParams.get('to');

  const [inputFormat, setInputFormat] = useState(fromParam || 'IMAGE');
  const [outputFormat, setOutputFormat] = useState(toParam || 'WebP');
  const [inputDropdownOpen, setInputDropdownOpen] = useState(false);
  const [outputDropdownOpen, setOutputDropdownOpen] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const [uploadedFile, setUploadedFile] = useState(null);
  const [uploadedPreview, setUploadedPreview] = useState(null);
  const [converting, setConverting] = useState(false);
  const [converted, setConverted] = useState(false);
  const [convertedImage, setConvertedImage] = useState(null);
  const [error, setError] = useState(null);

  const inputFormats = ['IMAGE', 'JPG', 'PNG', 'WebP', 'AVIF', 'GIF', 'BMP'];
  const outputFormats = ['WebP', 'JPG', 'PNG', 'AVIF'];
  const MAX_FILE_SIZE = 20 * 1024 * 1024; // 20MB in bytes
  const formatAcceptMap = {
    IMAGE: 'image/*,.jpg,.jpeg,.png,.webp,.avif,.gif,.bmp,.tiff,.svg',
    JPG: 'image/jpeg,.jpg,.jpeg',
    PNG: 'image/png,.png',
    WebP: 'image/webp,.webp',
    AVIF: 'image/avif,.avif',
    GIF: 'image/gif,.gif',
    BMP: 'image/bmp,.bmp'
  };
  const formatFileExtensions = {
    IMAGE: ['jpg', 'jpeg', 'png', 'webp', 'avif', 'gif', 'bmp', 'tiff', 'tif', 'svg'],
    JPG: ['jpg', 'jpeg'],
    PNG: ['png'],
    WebP: ['webp'],
    AVIF: ['avif'],
    GIF: ['gif'],
    BMP: ['bmp']
  };

  const availableOutputFormats = outputFormats.filter(format => format !== inputFormat);

  // Update formats if URL params change
  useEffect(() => {
    const fromUpper = fromParam?.toUpperCase();
    const toUpper = toParam?.toUpperCase();
    
    if (fromUpper && inputFormats.includes(fromUpper)) {
      setInputFormat(fromUpper);
    }
    if (toUpper && outputFormats.includes(toUpper)) {
      setOutputFormat(toUpper);
    }
  }, [fromParam, toParam]);

  const handleInputSelect = (format) => {
    setInputFormat(format);
    setInputDropdownOpen(false);
    // Only adjust output if the selected input format is a specific format that matches output
    if (format !== 'IMAGE' && format === outputFormat) {
      const newOutput = availableOutputFormats[0];
      setOutputFormat(newOutput);
    }
  };

  const handleOutputSelect = (format) => {
    setOutputFormat(format);
    setOutputDropdownOpen(false);
  };

  const isFileAllowedForInputFormat = (file) => {
    // If "IMAGE" is selected, accept all image types
    if (inputFormat === 'IMAGE') {
      return file.type.startsWith('image/');
    }

    // For specific formats, validate extension and MIME type
    const extensions = formatFileExtensions[inputFormat] || [];
    const extension = file.name.split('.').pop().toLowerCase();
    const parsedType = file.type.toLowerCase();

    const isExtensionValid = extensions.includes(extension);
    const isMimeValid = {
      JPG: parsedType === 'image/jpeg',
      PNG: parsedType === 'image/png',
      WebP: parsedType === 'image/webp',
      AVIF: parsedType === 'image/avif',
      GIF: parsedType === 'image/gif',
      BMP: parsedType === 'image/bmp'
    }[inputFormat];

    return isExtensionValid && isMimeValid;
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = () => {
    setIsDragging(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setIsDragging(false);
    const file = e.dataTransfer.files[0];

    if (!file || !file.type.startsWith('image/')) {
      setError('Please choose a valid image file.');
      return;
    }

    if (!isFileAllowedForInputFormat(file)) {
      if (inputFormat === 'IMAGE') {
        setError('Please upload a valid image file.');
      } else {
        setError(`Please upload a ${inputFormat} image only.`);
      }
      return;
    }

    if (file.size > MAX_FILE_SIZE) {
      setError('File size exceeds 20MB limit. Please choose a smaller image.');
      return;
    }

    setError(null);
    handleFileUpload(file);
  };

  const handleFileSelect = (e) => {
    const file = e.target.files[0];

    if (!file || !file.type.startsWith('image/')) {
      setError('Please choose a valid image file.');
      return;
    }

    if (!isFileAllowedForInputFormat(file)) {
      if (inputFormat === 'IMAGE') {
        setError('Please upload a valid image file.');
      } else {
        setError(`Please upload a ${inputFormat} image only.`);
      }
      return;
    }

    if (file.size > MAX_FILE_SIZE) {
      setError('File size exceeds 20MB limit. Please choose a smaller image.');
      return;
    }

    setError(null);
    handleFileUpload(file);
  };

  const handleFileUpload = (file) => {
    setUploadedFile(file);
    setConverted(false);
    setConvertedImage(null);

    const reader = new FileReader();
    reader.onload = (e) => {
      setUploadedPreview(e.target.result);
      // Auto-start conversion after preview is loaded
      setTimeout(() => {
        convertImageAuto(e.target.result, file);
      }, 500);
    };
    reader.readAsDataURL(file);
  };

  const convertImageAuto = async (previewSrc, file) => {
    setConverting(true);
    setConverted(false);

    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    const img = new Image();

    img.onload = () => {
      canvas.width = img.width;
      canvas.height = img.height;
      ctx.drawImage(img, 0, 0);

      let mimeType;
      let quality = 0.95;

      switch (outputFormat) {
        case 'PNG':
          mimeType = 'image/png';
          break;
        case 'JPG':
          mimeType = 'image/jpeg';
          break;
        case 'WebP':
          mimeType = 'image/webp';
          break;
        case 'AVIF':
          mimeType = 'image/avif';
          break;
        default:
          mimeType = 'image/png';
      }

      // Ensure minimum 3 seconds processing animation
      setTimeout(() => {
        canvas.toBlob(
          (blob) => {
            const url = URL.createObjectURL(blob);
            setConvertedImage({ url, blob });
            setConverting(false);
            setConverted(true);
          },
          mimeType,
          quality
        );
      }, 3000);
    };

    img.src = previewSrc;
  };

  const convertImage = async () => {
    setConverting(true);
    setConverted(false);

    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    const img = new Image();

    img.onload = () => {
      canvas.width = img.width;
      canvas.height = img.height;
      ctx.drawImage(img, 0, 0);

      let mimeType;
      let quality = 0.95;

      switch (outputFormat) {
        case 'PNG':
          mimeType = 'image/png';
          break;
        case 'JPG':
          mimeType = 'image/jpeg';
          break;
        case 'WebP':
          mimeType = 'image/webp';
          break;
        default:
          mimeType = 'image/png';
      }

      // Ensure minimum 3 seconds processing animation
      setTimeout(() => {
        canvas.toBlob(
          (blob) => {
            const url = URL.createObjectURL(blob);
            setConvertedImage({ url, blob });
            setConverting(false);
            setConverted(true);
          },
          mimeType,
          quality
        );
      }, 3000);
    };

    img.src = uploadedPreview;
  };

  const downloadImage = () => {
    if (convertedImage) {
      const link = document.createElement('a');
      const fileName = uploadedFile.name.replace(/\.[^/.]+$/, '');
      link.href = convertedImage.url;
      link.download = `${fileName}.${outputFormat.toLowerCase()}`;
      link.click();
    }
  };

  const resetUpload = () => {
    setUploadedFile(null);
    setUploadedPreview(null);
    setConverted(false);
    setConvertedImage(null);
    setConverting(false);
    setError(null);
  };

  const getFileFormat = (file) => {
    const ext = file.name.split('.').pop().toUpperCase();
    // Normalize JPEG to JPG for display
    return ext === 'JPEG' ? 'JPG' : ext;
  };

  return (
    <div className="workspace-page">
      <SEO 
        title="Image Converter Online – JPG, PNG, WEBP & More"
        description="Convert images between JPG, PNG, WEBP, GIF & more formats free online. Fast, high-quality image conversion with no quality loss."
        keywords="image converter, jpg to png, convert image online, png to webp"
        path="/image-converter"
        schema={imageConverterSchemas}
      />
      <div className="workspace-container">
        <motion.div
          className="workspace-header"
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
        >
          <h1 className="workspace-title">Image Converter</h1>
          <p className="workspace-subtitle">Convert your images between different formats instantly</p>
        </motion.div>

        <div className="workspace-content-wrapper">
          {/* Left Decorative Image */}
          <img 
            src="/img-3.png" 
            alt="Decorative" 
            className="workspace-decorative-img workspace-decorative-img-left"
          />

          {/* Right Decorative Image */}
          <img 
            src="/img-4.png" 
            alt="Decorative" 
            className="workspace-decorative-img workspace-decorative-img-right"
          />

          <div className="workspace-main-content">
            <motion.div
              className="format-selector"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.1 }}
            >
          <div className="format-dropdown-wrapper">
            <label className="format-label">From</label>
            <div className="custom-dropdown">
              <motion.button
                className="dropdown-trigger"
                onClick={() => setInputDropdownOpen(!inputDropdownOpen)}
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
              >
                <span className="dropdown-value">{inputFormat}</span>
                <motion.div
                  animate={{ rotate: inputDropdownOpen ? 180 : 0 }}
                  transition={{ duration: 0.3 }}
                >
                  <FiChevronDown />
                </motion.div>
              </motion.button>

              <AnimatePresence>
                {inputDropdownOpen && (
                  <motion.div
                    className="dropdown-menu"
                    initial={{ opacity: 0, y: -10 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -10 }}
                    transition={{ duration: 0.2 }}
                  >
                    {inputFormats.map((format) => (
                      <motion.div
                        key={format}
                        className={`dropdown-item ${format === inputFormat ? 'active' : ''}`}
                        onClick={() => handleInputSelect(format)}
                        whileHover={{ backgroundColor: 'rgba(234, 88, 12, 0.1)' }}
                      >
                        <BsFileImage />
                        <span>{format}</span>
                      </motion.div>
                    ))}
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          </div>

          <motion.div
            className="format-arrow"
            animate={{ x: [0, 10, 0] }}
            transition={{ duration: 2, repeat: Infinity, ease: 'easeInOut' }}
          >
            →
          </motion.div>

          <div className="format-dropdown-wrapper">
            <label className="format-label">To</label>
            <div className="custom-dropdown">
              <motion.button
                className="dropdown-trigger"
                onClick={() => setOutputDropdownOpen(!outputDropdownOpen)}
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
              >
                <span className="dropdown-value">{outputFormat}</span>
                <motion.div
                  animate={{ rotate: outputDropdownOpen ? 180 : 0 }}
                  transition={{ duration: 0.3 }}
                >
                  <FiChevronDown />
                </motion.div>
              </motion.button>

              <AnimatePresence>
                {outputDropdownOpen && (
                  <motion.div
                    className="dropdown-menu"
                    initial={{ opacity: 0, y: -10 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -10 }}
                    transition={{ duration: 0.2 }}
                  >
                    {availableOutputFormats.map((format) => (
                      <motion.div
                        key={format}
                        className={`dropdown-item ${format === outputFormat ? 'active' : ''}`}
                        onClick={() => handleOutputSelect(format)}
                        whileHover={{ backgroundColor: 'rgba(234, 88, 12, 0.1)' }}
                      >
                        <BsFileImage />
                        <span>{format}</span>
                      </motion.div>
                    ))}
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          </div>
        </motion.div>

        <AnimatePresence mode="wait">
          {error && (
            <motion.div
              className="error-message"
              initial={{ opacity: 0, y: -20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              transition={{ duration: 0.4 }}
            >
              <div className="error-content">
                <span className="error-icon">⚠️</span>
                <p className="error-text">{error}</p>
                <motion.button
                  className="error-close"
                  onClick={() => setError(null)}
                  whileHover={{ scale: 1.1 }}
                  whileTap={{ scale: 0.9 }}
                >
                  ×
                </motion.button>
              </div>
            </motion.div>
          )}

          {!uploadedFile ? (
            <motion.div
              className={`upload-area ${isDragging ? 'dragging' : ''}`}
              onDragOver={handleDragOver}
              onDragLeave={handleDragLeave}
              onDrop={handleDrop}
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -30 }}
              transition={{ duration: 0.6 }}
              key="upload"
            >
              <div className="upload-content">
                <motion.div
                  className="upload-icon-wrapper"
                  animate={{ y: [0, -10, 0] }}
                  transition={{ duration: 2, repeat: Infinity, ease: 'easeInOut' }}
                >
                  <FiUploadCloud className="upload-icon" />
                </motion.div>

                <h3 className="upload-title">Upload Image</h3>
                <p className="upload-description">Drag and drop your image here</p>

                <label htmlFor="file-input">
                  <motion.div
                    className="upload-button"
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                  >
                    <BsFileImage />
                    Choose File
                  </motion.div>
                </label>
                <input
                  id="file-input"
                  type="file"
                  accept={formatAcceptMap[inputFormat]}
                  onChange={handleFileSelect}
                  style={{ display: 'none' }}
                />
              </div>

              <div className="upload-glow"></div>
            </motion.div>
          ) : (
            <motion.div
              className="preview-container"
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -30 }}
              transition={{ duration: 0.6 }}
              key="preview"
            >
              <div className="preview-cards">
                <motion.div
                  className="preview-card"
                  initial={{ opacity: 0, x: -30 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.5, delay: 0.1 }}
                >
                  <div className="preview-image-wrapper">
                    <img src={uploadedPreview} alt="Original" className="preview-image" />
                  </div>
                  <div className="preview-info">
                    <p className="preview-title">{uploadedFile.name}</p>
                    <p className="preview-format">{getFileFormat(uploadedFile)}</p>
                  </div>
                </motion.div>

                <motion.div
                  className="preview-arrow"
                  initial={{ opacity: 0, scale: 0.5 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ duration: 0.5, delay: 0.3 }}
                >
                  →
                </motion.div>

                <motion.div
                  className="preview-card"
                  initial={{ opacity: 0, x: 30 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.5, delay: 0.1 }}
                >
                  {!converting && !converted && (
                    <div className="preview-placeholder">
                      <BsFileImage className="placeholder-icon" />
                      <p className="placeholder-text">{outputFormat}</p>
                    </div>
                  )}

                  {converting && (
                    <div className="converting-state">
                      <motion.div
                        className="spinner"
                        animate={{ rotate: 360 }}
                        transition={{ duration: 1, repeat: Infinity, ease: 'linear' }}
                      />
                      <p className="converting-text">Converting...</p>
                    </div>
                  )}

                  {converted && convertedImage && (
                    <>
                      <div className="preview-image-wrapper">
                        <img src={convertedImage.url} alt="Converted" className="preview-image" />
                        <motion.div
                          className="success-badge"
                          initial={{ scale: 0 }}
                          animate={{ scale: 1 }}
                          transition={{ type: 'spring', stiffness: 200 }}
                        >
                          <FiCheck />
                        </motion.div>
                      </div>
                      <div className="preview-info">
                        <p className="preview-title">{uploadedFile.name.replace(/\.[^/.]+$/, '')}.{outputFormat.toLowerCase()}</p>
                        <p className="preview-format">{outputFormat}</p>
                        <p className="preview-converted-format">Converted from {getFileFormat(uploadedFile)} to {outputFormat}</p>
                      </div>
                    </>
                  )}
                </motion.div>
              </div>

              <motion.div
                className="action-buttons"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: 0.4 }}
              >
                {converted && (
                  <motion.button
                    className="convert-button download"
                    onClick={downloadImage}
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    initial={{ scale: 0.8 }}
                    animate={{ scale: 1 }}
                  >
                    <FiDownload />
                    Download {outputFormat}
                  </motion.button>
                )}

                <motion.button
                  className="reset-button"
                  onClick={resetUpload}
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                >
                  Upload New Image
                </motion.button>
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Workspace;
